import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, output, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MenuItem } from 'primeng/api';
import { AgGridAngular } from 'ag-grid-angular';
import {
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IServerSideDatasource,
  RowClassRules,
  RowSelectionOptions,
} from 'ag-grid-community';
import { DateNavigationService } from '../../../services/date-navigation.service';
import {
  countSelectedCells,
  formatCellForClipboard,
  noRowsOverlay,
  READ_ONLY_DEFAULT_COL_DEF,
  syncNoRowsOverlay,
} from '../../../utils/grid/grid-presets';
import { FullscreenDirective } from '../../../directives/fullscreen.directive';
import { ClipboardColumnType, createClipboardCellParser, isKeystrokeClaimedElsewhere } from '../grid-clipboard/grid-clipboard.utils';
import { withRemoteChangeFlash } from '../../../utils/grid/remote-change-flash';
import { parseTimestampFilterFromUrl, serializeTimestampFilterForUrl } from '../../../utils/timestamp-filter-url.utils';
import { CesAssetBreadcrumbComponent } from '../ces-asset-breadcrumb/ces-asset-breadcrumb.component';
import { CesDateNavigationComponent } from '../ces-date-navigation/ces-date-navigation.component';
import { CesButtonSecondaryComponent } from '../ces-buttons/ces-button-secondary.component';
import { CesGridSkeletonComponent } from '../skeletons/grid-skeleton.component';
import { agGridLocale } from '../../../utils/grid/grid-presets';

const SSRM_BLOCK_SIZE = 100;

// Single row identity shared by getRowId and the remote-change flash diff.
function rowTimestampKey(row: { timestamp: Date | string }): string {
  const ts = row.timestamp;
  return ts instanceof Date ? ts.toISOString() : String(ts);
}

@Component({
  selector: 'ces-time-series-detail',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AgGridAngular,
    CesAssetBreadcrumbComponent,
    CesDateNavigationComponent,
    CesButtonSecondaryComponent,
    CesGridSkeletonComponent,
    FullscreenDirective,
    TranslocoModule,
  ],
  host: {
    '(document:keydown.escape)': 'onDocumentEscape($event)',
  },
  templateUrl: './ces-time-series-detail.component.html',
  styleUrl: './ces-time-series-detail.component.scss',
})
export class CesTimeSeriesDetailComponent<TRow extends { timestamp: Date | string }> implements OnInit {
  readonly dateNav = inject(DateNavigationService);
  private readonly translate = inject(TranslocoService);
  readonly localeText = agGridLocale(this.translate.getActiveLang());
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  readonly title = input.required<string>();
  readonly externalLinkUrl = input<string | null>(null);
  readonly frequencyTag = input<string | null>(null);
  readonly breadcrumbTrail = input.required<MenuItem[]>();
  readonly columnDefs = input.required<ColDef<TRow>[]>();
  readonly datasource = input.required<IServerSideDatasource>();
  readonly isLoading = input(false);
  readonly loadError = input(false);
  readonly isReady = input(true);
  readonly isEmpty = input(false);
  readonly rowSelection = input<RowSelectionOptions | undefined>(undefined);
  readonly rowClassRules = input<RowClassRules<TRow> | undefined>(undefined);
  /** Per-column Excel-paste parsing for hosts with editable columns; omitted = raw strings pass through. */
  readonly clipboardColumnType = input<((colId: string) => ClipboardColumnType) | undefined>(undefined);

  readonly copyValues = output<void>();
  readonly cellsCopied = output<number>();
  readonly windowChanged = output<void>();
  /** A window came from the URL: the host must arm its grid without persisting these dates. */
  readonly windowFromUrl = output<void>();
  readonly selectionChanged = output<TRow[]>();

  readonly defaultColDef = READ_ONLY_DEFAULT_COL_DEF;

  readonly hasActiveFilters = signal(false);
  readonly selectedCellCount = signal(0);
  readonly showNeedsRefresh = computed(() => !this.isLoading() && !this.loadError() && !this.isReady());
  // Nothing copyable while the grid is hidden behind a loading/error/needs-refresh/empty state.
  readonly copyDisabled = computed(() => this.isLoading() || this.loadError() || !this.isReady() || this.isEmpty());

  readonly copyButtonLabel = computed(() => {
    const count = this.selectedCellCount();
    return count > 0
      ? this.translate.translate('common.buttons.copySelection', { count })
      : this.translate.translate('common.buttons.copyAllValues');
  });

  readonly showStaleWarning = computed(() => {
    if (this.isLoading() || this.loadError() || !this.isReady()) return false;
    return this.dateNav.hasAppliedDates() && this.dateNav.hasPendingChanges();
  });

  readonly gridOptions: GridOptions<TRow> = {
    theme: 'legacy',
    animateRows: false,
    suppressColumnVirtualisation: true,
    // SSRM never binds rowData, so AG Grid's auto loading overlay would never hide;
    // the component owns the loading skeleton, and a filter matching nothing then
    // surfaces the no-rows overlay instead of collapsing the grid.
    loading: false,
    overlayNoRowsTemplate: noRowsOverlay(this.translate.translate('common.messages.noData')),
    // Inert without tooltip getters; AG Grid's 2 s default is too slow for them.
    tooltipShowDelay: 500,
    cellSelection: { suppressMultiRanges: false },
    processCellForClipboard: formatCellForClipboard,
    // Pasted text must land as typed values (numbers, not strings) and Excel's trailing
    // blank line must not blank the row below the paste target.
    suppressLastEmptyLineOnPaste: true,
    processCellFromClipboard: createClipboardCellParser(colId => this.clipboardColumnType()?.(colId) ?? { kind: 'passthrough' }),
    onFilterChanged: () => this.updateFilterState(),
    // SSRM does not raise the no-rows overlay on its own; drive it from the row count so a
    // filter (or period) matching nothing shows a message instead of a blank grid — and the
    // grid (with its filters) stays mounted so "Effacer" can always recover it.
    onModelUpdated: event => syncNoRowsOverlay(event.api),
    onGridPreDestroyed: () => {
      this.gridApi = null;
    },
    // Keeps the checkbox column ahead of the pinned date column; inert when no rowSelection is configured.
    selectionColumnDef: { pinned: 'left' },
    // Remote-update flash paced like the campaign grid's 3 s highlight (2 s hold + 1 s fade);
    // fires on the value columns that opt in via enableCellChangeFlash.
    cellFlashDuration: 2000,
    cellFadeDuration: 1000,
    rowModelType: 'serverSide',
    cacheBlockSize: SSRM_BLOCK_SIZE,
    maxBlocksInCache: 10,
    // Un drag rapide de la scrollbar ne charge que le bloc d'arrivée : les
    // blocs traversés sont ignorés, le bloc visé s'affiche plus vite.
    blockLoadDebounceMillis: 50,
    serverSideInitialRowCount: 1,
    suppressMultiSort: true,
    getRowId: params => rowTimestampKey(params.data),
  };

  private gridApi: GridApi<TRow> | null = null;

  // A `filters=timestamp:` link states the days to look at, so it doubles as the fetch window and
  // arms the grid: a link lands on data instead of the refresh prompt. Applied like a refresh but
  // never saved — a visit that only came through a link must leave the host's own date preference
  // untouched, since that preference is shared with every other time-series screen.
  ngOnInit(): void {
    const [from, to] = parseTimestampFilterFromUrl(this.route.snapshot.queryParams) ?? [null, null];
    if (!from || !to) return;

    this.dateNav.setDates(from, to);
    this.dateNav.applyDates();
    this.windowFromUrl.emit();
  }

  onGridReady(event: GridReadyEvent<TRow>): void {
    this.gridApi = event.api;
    this.applyTimestampFilterFromUrl(event.api);
    // Silent refreshes swap SSRM block data without any native flash; the wrapper diffs each
    // incoming row against the node it replaces and flashes the opted-in value columns.
    event.api.setGridOption(
      'serverSideDatasource',
      withRemoteChangeFlash<TRow>(this.datasource(), {
        getApi: () => this.gridApi,
        getFlashFields: () =>
          this.columnDefs()
            .filter(col => col.enableCellChangeFlash && typeof col.field === 'string')
            .map(col => col.field as string),
        getRowKey: row => rowTimestampKey(row),
      }),
    );
  }

  onSelectionChanged(): void {
    this.selectionChanged.emit(this.gridApi?.getSelectedRows() ?? []);
  }

  refreshData(purge = false): void {
    this.gridApi?.refreshServerSide({ purge });
  }

  isEditing(): boolean {
    return (this.gridApi?.getEditingCells().length ?? 0) > 0;
  }

  // rowClassRules only re-evaluate on a redraw, never on refreshCells.
  redrawRows(): void {
    this.gridApi?.redrawRows();
  }

  // Re-runs only the given columns' valueGetter/cellRenderer (default: all). Scoped to 'status'
  // it flashes/clears the "Calcul en cours" badge without disturbing an open value editor.
  refreshCells(columns?: string[]): void {
    this.gridApi?.refreshCells(columns ? { force: true, columns } : { force: true });
  }

  deselectAll(): void {
    this.gridApi?.deselectAll();
  }

  onCellSelectionChanged(): void {
    this.selectedCellCount.set(this.gridApi ? countSelectedCells(this.gridApi) : 0);
  }

  clearSelection(): void {
    this.gridApi?.clearCellSelection();
    this.gridApi?.clearFocusedCell();
    this.selectedCellCount.set(0);
  }

  /** Escape meant for another layer (filter field, dialog, datepicker panel...) must not drop the selection. */
  onDocumentEscape(event: KeyboardEvent): void {
    if (!isKeystrokeClaimedElsewhere(event)) this.clearSelection();
  }

  onCopyClick(): void {
    const count = this.selectedCellCount();
    if (count > 0 && this.gridApi) {
      this.gridApi.copySelectedRangeToClipboard();
      this.cellsCopied.emit(count);
      return;
    }
    this.copyValues.emit();
  }

  clearAllFilters(): void {
    this.gridApi?.setFilterModel(null);
  }

  refresh(): void {
    this.dateNav.applyDates();
    this.windowChanged.emit();
    this.gridApi?.refreshServerSide({ purge: true });
  }

  private updateFilterState(): void {
    this.hasActiveFilters.set(this.gridApi?.isAnyFilterPresent() ?? false);
    this.syncTimestampFilterToUrl();
  }

  // The date filter round-trips through the URL exactly like the PrimeNG grids
  // (`filters=timestamp:dd.mm.yyyy-dd.mm.yyyy`), so filtered views can be shared.
  private syncTimestampFilterToUrl(): void {
    const model = this.gridApi?.getFilterModel() as Record<string, unknown> | undefined;
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { filters: serializeTimestampFilterForUrl(model?.['timestamp']) },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private applyTimestampFilterFromUrl(api: GridApi<TRow>): void {
    const range = parseTimestampFilterFromUrl(this.route.snapshot.queryParams);
    if (range) api.setFilterModel({ timestamp: range });
  }
}
